import styled from '@emotion/styled';
import { Avatar, Icon, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import LocationMarker from '@/../public/icons/LocationMarker.png';
import { ReactNode } from 'react';
import { ExperienceTags } from '@/components/cards/experience-tags/experience-tags';
import { MediaContainer } from '@/components/media-container/media-container';
import { ExperienceCardFieldsFragment, ExperienceType } from '@/generated/graphql';
import { useSelectedMarker } from '../hooks/use-selected-marker';
import { ExperienceAvailabilityTime } from '@/components/cards/experience-card/components/experience-availability-time/experience-availability-time';
import { ExperienceEventTime } from '@/components/cards/experience-card/components/experience-event-time/experience-event-time';

const CardWrapper = styled.div`
  display: block;
  width: 100%;
  text-decoration: none;
  transition: box-shadow 0.2s;
  box-shadow: 0px 2.76191px 13.8096px 2.76191px rgba(180, 180, 180, 0.25);
  border-radius: 23.4px;
  margin-bottom: 40px;
  outline: 0px solid #ffcf31;
  outline-offset: 5px;
  transition: outline 0.2s ease-in-out;
  &:hover {
    outline: 3px solid #ffcf31;
  }
  scroll-margin-top: 100px;
`;
const StyledLink = styled(Link)`
  text-decoration: none;
  & * {
    text-decoration: none;
  }
`;

const Container = styled.div`
  min-height: 176px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  position: relative;
  background-color: #ffffff;
  border-radius: 23.476px;
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;
const ImageDiv = styled.div`
  width: fit-content;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  @media (max-width: 1024px) {
    width: 100%;
    flex-direction: column;
  }
`;
const HostProPicContainer = styled(MediaContainer)`
  border-radius: 60% 70% 70% 60%;
  width: 48px;
  height: 48px;
  border: 5px solid #ffffff;
  margin-left: -30px;
  z-index: 1;
  @media (max-width: 1024px) {
    margin-left: auto;
    margin-top: -24px;
    margin-right: auto;
  }
`;
const MainImageContainer = styled(MediaContainer)`
  height: 146px;
  width: 200px;
  border-radius: 20px;
  @media (max-width: 1024px) {
    width: 100%;
  }
`;
const DetailsDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  @media (max-width: 1024px) {
    width: 100%;
    align-items: center;
  }
`;
const Title = styled(Typography)`
  color: #251628;

  /* Header 6 */
  font-family: var(--font-work-sans), sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 136.8%; /* 21.888px */
  text-align: left;
`;
const HostText = styled(Typography)`
  color: #767676;

  /* Body Small */
  font-family: var(--font-work-sans);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%; /* 25.6px */
`;

const LocationText = styled(Typography)`
  color: #129661;
  font-family: var(--font-work-sans);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 136.8%; /* 21.888px */
`;
const LocationTags = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;
const StyledAvatar = styled(Avatar)`
  border-radius: 0;
  & > img {
    object-fit: contain;
  }
`;

const LocationMarkerIcon = (
  <StyledAvatar
    src={LocationMarker.src}
    style={{ borderRadius: 0, objectFit: 'contain', height: 20, width: 20 }}
  ></StyledAvatar>
);

const TimeContainer = styled.div`
  margin-top: 8px;
`;

export interface WideCardProps {
  experience?: ExperienceCardFieldsFragment;
  children?: ReactNode;
}
export const ExploreOneOnOneCard = ({ experience }: WideCardProps) => {
  const image = experience?.images?.edges[0]?.node?.file;
  const host = experience?.host?.profile;
  const { selectedMarker } = useSelectedMarker();
  const type = experience?.type;
  const nextEvent = type === ExperienceType.Event && experience?.nextEvent;
  const nextAvailability = type === ExperienceType.Schedule && experience?.nextAvailability;

  const isSelected = selectedMarker === experience?.id ? true : false;
  if (!experience) {
    return null;
  }
  return (
    <StyledLink href={`/experiences/${experience?.id}`}>
      <CardWrapper style={isSelected ? { outline: '3px solid #ffcf31' } : {}} id={experience?.id}>
        <Container>
          <ImageDiv>
            <MainImageContainer>
              {image?.url && (
                <Image
                  src={image?.url}
                  alt="Host Profile Image"
                  fill
                  style={{ objectFit: 'cover' }}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </MainImageContainer>
            <HostProPicContainer>
              {host?.avatar?.url && (
                <Image
                  src={host?.avatar?.url}
                  alt="Host Profile Image"
                  fill
                  style={{ objectFit: 'cover' }}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </HostProPicContainer>
          </ImageDiv>
          <DetailsDiv>
            <ExperienceTags experience={experience} />
            <Title>{experience?.title}</Title>
            <HostText>
              {'Hosted by'}&nbsp;
              <span style={{ color: '#4865B7' }}>{host?.nickname}</span>
            </HostText>
            <LocationTags>
              <Icon>{LocationMarkerIcon}</Icon>
              <LocationText>{`${experience?.locality}, BC`}</LocationText>
            </LocationTags>
            <TimeContainer>
              {nextEvent && <ExperienceEventTime time={nextEvent} />}
              {nextAvailability && <ExperienceAvailabilityTime time={nextAvailability} />}
            </TimeContainer>
          </DetailsDiv>
        </Container>
      </CardWrapper>
    </StyledLink>
  );
};
